import React from "react";
import { Box, Typography } from "@material-ui/core";
import { Route, RouteComponentProps } from "react-router-dom";
import {
  ActionButtonWithRoute,
  Icon,
  IconName,
  FunnelNewTag,
  VacationRentalNewTag,
  FunnelNewTagType,
} from "halifax";
import clsx from "clsx";
import { getConfigTenant, isCorpTenant } from "@capone/common";
import {
  CORP_MARKETPLACE_PACKAGES_NAME,
  CORP_MARKETPLACE_PACKAGES_NAME_VARIANTS,
  TRIP_BUILDER,
  HOTELS_FLIGHTS,
} from "@capone/experiments";
import {
  PATH_FLIGHTS,
  PATH_HOTELS,
  PATH_CARS,
  PATH_PREMIER_COLLECTION,
  PATH_PACKAGES,
  PATH_STAYS,
  PATH_EXPERIENCES,
} from "../../utils/urlPaths";
import "./styles.scss";
import { LandingPageType } from "../MobileLandingPage";
import {
  useExperiments,
  getExperimentVariantCustomVariants,
  AVAILABLE,
  getExperimentVariant,
  VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT,
  PACKAGES_EXPERIMENT,
  PACKAGES_EXPERIMENT_VARIANTS,
  SELF_FUNDED,
  NOT_SELF_FUNDED,
  LUXURY_VACATION_RENTAL_FUNNEL_LAUNCH,
  STAYS_HOMEPAGE,
  EXPERIENCES_EXPERIMENT,
  LC_GATING_EXPERIMENT,
  STAYS_VACATION_RENTALS_AWARENESS_MODAL,
  PREMIER_COLLECTION_EXPERIMENT,
  CONTROL,
} from "../../context/experiments";
import { HowPackagesWork } from "../../../../../cap1-modules/packages-module/src/modules/search/components/HowPackagesWork";
import { config } from "../../api/config";

export interface MobileLandingPageProps {
  type?: LandingPageType;
}
const LandingPageNavigation = ({ type }: MobileLandingPageProps) => {
  const expState = useExperiments();

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT
    ) === AVAILABLE;

  const isLifestyleCollectionEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
    AVAILABLE;

  const isLifestyleOnlyCollectionEnabled =
    isLifestyleCollectionEnabled && !isPremierCollectionEnabled;

  const showPremiumFunnel =
    isLifestyleCollectionEnabled && !isCorpTenant(config.TENANT);

  const isVRForNonPremiumCardHoldersEnabled =
    getExperimentVariant(
      expState.experiments,
      VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT
    ) === AVAILABLE;

  const packagesExperimentVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    PACKAGES_EXPERIMENT,
    PACKAGES_EXPERIMENT_VARIANTS
  );

  const isCorporate = isCorpTenant(getConfigTenant());

  const corpMarketplacePackagesNameVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    CORP_MARKETPLACE_PACKAGES_NAME,
    CORP_MARKETPLACE_PACKAGES_NAME_VARIANTS
  );

  const getPackagesDisplayName = () => {
    if (!isCorporate) {
      return "Packages";
    }

    switch (corpMarketplacePackagesNameVariant) {
      case TRIP_BUILDER:
        return "Trip Builder";
      case HOTELS_FLIGHTS:
        return "Hotels + Flights";
      case CONTROL:
      default:
        return "Hotels + Flights";
    }
  };

  const vacationFunnelLaunchEnabled =
    getExperimentVariant(
      expState.experiments,
      LUXURY_VACATION_RENTAL_FUNNEL_LAUNCH
    ) === AVAILABLE;

  const isPackagesExperiment = [SELF_FUNDED, NOT_SELF_FUNDED].includes(
    packagesExperimentVariant
  );

  const isExperiencesExperiment =
    getExperimentVariant(expState.experiments, EXPERIENCES_EXPERIMENT) ===
    AVAILABLE;

  const staysHomepageEnabled =
    getExperimentVariant(expState.experiments, STAYS_HOMEPAGE) === AVAILABLE;

  const staysVacationRentalsAwarenessModalEnabled =
    getExperimentVariant(
      expState.experiments,
      STAYS_VACATION_RENTALS_AWARENESS_MODAL
    ) === AVAILABLE;
  const staysVRAwarenessModalLocalStorageKey = "vr_awareness_modal_seen";
  const staysVRAwarenessModalSeen =
    localStorage.getItem(staysVRAwarenessModalLocalStorageKey) != null;

  const getSearchButtonText = (type: LandingPageType) => {
    if (type === LandingPageType.PREMIUM_STAYS) {
      if (isPremierCollectionEnabled || isVRForNonPremiumCardHoldersEnabled) {
        return "premium stays";
      }
      if (isLifestyleOnlyCollectionEnabled) {
        return "lifestyle hotels";
      }
      return "premier hotels";
    }

    if (type === LandingPageType.PACKAGES && isCorporate) {
      switch (corpMarketplacePackagesNameVariant) {
        case TRIP_BUILDER:
          return "trips";
        case HOTELS_FLIGHTS:
          return "hotels + flights";
        case CONTROL:
        default:
          return "hotels + flights";
      }
    }

    return type;
  };

  return (
    <>
      <Box
        className={clsx("landing-page-buttons", {
          "pc-enabled":
            showPremiumFunnel ||
            isExperiencesExperiment ||
            isPackagesExperiment,
        })}
      >
        <Route
          render={(browserRouterProps: RouteComponentProps) => (
            <ActionButtonWithRoute
              {...browserRouterProps}
              className={clsx("full-width-landing-button", {
                hotels: type === "hotels" || type === "stays",
              })}
              message={
                <Box className="landing-button-content-wrapper">
                  <Icon name={IconName.HotelFunnelIcon} />

                  <p>{staysHomepageEnabled ? "Stays" : "Hotels"}</p>
                  {staysVacationRentalsAwarenessModalEnabled &&
                    !staysVRAwarenessModalSeen && (
                      <FunnelNewTag
                        enabled
                        type={FunnelNewTagType.STAYS_FUNNEL_SEARCHES}
                      />
                    )}
                </Box>
              }
              disabled={false}
              onClick={() => {
                const searchQuery = browserRouterProps.location.search;
                const path = `${
                  staysHomepageEnabled ? PATH_STAYS : PATH_HOTELS
                }${searchQuery ? searchQuery : ""}`;
                browserRouterProps.history.push(path);
              }}
              ariaLabelText={staysHomepageEnabled ? "Stays" : "Hotels"}
            />
          )}
        />

        <Route
          render={(browserRouterProps: RouteComponentProps) => (
            <ActionButtonWithRoute
              {...browserRouterProps}
              className={clsx("full-width-landing-button", {
                flights: type === "flights",
              })}
              message={
                <Box className="landing-button-content-wrapper">
                  <Icon name={IconName.FlightFunnelIcon} />

                  <p>Flights</p>
                </Box>
              }
              disabled={false}
              onClick={() => browserRouterProps.history.push(PATH_FLIGHTS)}
              ariaLabelText="Flights"
            />
          )}
        />

        <Route
          render={(browserRouterProps: RouteComponentProps) => (
            <ActionButtonWithRoute
              {...browserRouterProps}
              className={clsx("full-width-landing-button", {
                cars: type === "cars",
              })}
              message={
                <Box className="landing-button-content-wrapper">
                  <Icon name={IconName.CarFunnelIcon} />

                  <p>Cars</p>
                </Box>
              }
              disabled={false}
              onClick={() => browserRouterProps.history.push(PATH_CARS)}
              ariaLabelText="Cars"
            />
          )}
        />
        {showPremiumFunnel && (
          <Route
            render={(browserRouterProps: RouteComponentProps) => (
              <ActionButtonWithRoute
                {...browserRouterProps}
                className={clsx("full-width-landing-button", {
                  "premier-collection": type === "premium-stays",
                  "lifestyle-collection":
                    type === "premium-stays" &&
                    isLifestyleOnlyCollectionEnabled,
                })}
                message={
                  <Box className="landing-button-content-wrapper">
                    {isPremierCollectionEnabled ? (
                      <Icon name={IconName.StarIconWithLines} />
                    ) : (
                      <Icon name={IconName.HotelFunnelIcon} />
                    )}
                    {(() => {
                      if (
                        isPremierCollectionEnabled ||
                        isVRForNonPremiumCardHoldersEnabled
                      )
                        return (
                          <>
                            <p>Premium Stays</p>
                            <VacationRentalNewTag
                              enabled={vacationFunnelLaunchEnabled}
                            />
                          </>
                        );

                      if (isLifestyleOnlyCollectionEnabled)
                        return <p>Lifestyle Collection</p>;

                      return <p>Premier Collection</p>;
                    })()}
                  </Box>
                }
                disabled={false}
                onClick={() =>
                  browserRouterProps.history.push(PATH_PREMIER_COLLECTION)
                }
                ariaLabelText="Premium Collection"
              />
            )}
          />
        )}
        {!showPremiumFunnel &&
          isExperiencesExperiment && ( // If premium funnel isn't enabled, experience is placed before packages
            <Route
              render={(browserRouterProps: RouteComponentProps) => (
                <ActionButtonWithRoute
                  {...browserRouterProps}
                  className={clsx("full-width-landing-button", {
                    experiences: type === "activities",
                  })}
                  message={
                    <Box className="landing-button-content-wrapper">
                      <Box className="icons-wrapper">
                        <Icon name={IconName.ExperiencesIcon} />
                      </Box>
                      <p>Activities</p>
                      <FunnelNewTag
                        enabled
                        type={FunnelNewTagType.EXPERIENCES_FUNNEL_SEARCHES}
                      />
                    </Box>
                  }
                  disabled={false}
                  onClick={() =>
                    browserRouterProps.history.push(PATH_EXPERIENCES)
                  }
                  ariaLabelText="Activities"
                />
              )}
            />
          )}
        {isPackagesExperiment && (
          <Route
            render={(browserRouterProps: RouteComponentProps) => (
              <ActionButtonWithRoute
                {...browserRouterProps}
                className={clsx(
                  "full-width-landing-button",

                  (!showPremiumFunnel && isExperiencesExperiment) ||
                    (showPremiumFunnel && !isExperiencesExperiment)
                    ? "multi-column"
                    : "",
                  {
                    packages: type === "packages",
                  }
                )}
                message={
                  <Box className="landing-button-content-wrapper">
                    <Box className="icons-wrapper">
                      <Icon name={IconName.HotelFunnelIcon} />
                      <Typography className="icons-separator">+</Typography>
                      <Icon name={IconName.FlightFunnelIcon} />
                    </Box>

                    <p>{getPackagesDisplayName()}</p>

                    <FunnelNewTag
                      enabled={!isExperiencesExperiment}
                      className={
                        isCorporate ? "corp-new-funnel-tag" : undefined
                      }
                      type={FunnelNewTagType.PACKAGES_FUNNEL_SEARCHES}
                    />
                  </Box>
                }
                disabled={false}
                onClick={() => browserRouterProps.history.push(PATH_PACKAGES)}
                ariaLabelText="Packages"
              />
            )}
          />
        )}
        {showPremiumFunnel && isExperiencesExperiment && (
          <Route
            render={(browserRouterProps: RouteComponentProps) => (
              <ActionButtonWithRoute
                {...browserRouterProps}
                className={clsx(
                  "full-width-landing-button",
                  !isPackagesExperiment ? "multi-column" : "",
                  {
                    experiences: type === "activities",
                  }
                )}
                message={
                  <Box className="landing-button-content-wrapper">
                    <Box className="icons-wrapper">
                      <Icon name={IconName.ExperiencesIcon} />
                      <Typography className="experiences-icons-separator" />
                    </Box>

                    <p>Activities</p>

                    <FunnelNewTag
                      enabled
                      type={FunnelNewTagType.EXPERIENCES_FUNNEL_SEARCHES}
                    />
                  </Box>
                }
                disabled={false}
                onClick={() =>
                  browserRouterProps.history.push(PATH_EXPERIENCES)
                }
                ariaLabelText="Activities"
              />
            )}
          />
        )}
      </Box>
      <Route
        render={(browserRouterProps: RouteComponentProps) => (
          <Box className="search-bar-container">
            <ActionButtonWithRoute
              {...browserRouterProps}
              className={clsx("full-width-landing-button", "search-bar")}
              message={
                <>
                  <Icon name={IconName.MagnifyingGlass} />
                  <p>
                    Search for {getSearchButtonText(type as LandingPageType)}
                  </p>
                </>
              }
              disabled={false}
              onClick={() => {
                const searchQuery = browserRouterProps.location.search;
                const path = `/${type}/search/${
                  searchQuery ? searchQuery : ""
                }`;
                browserRouterProps.history.push(path);
              }}
              ariaLabelText={`Search for ${getSearchButtonText(
                type as LandingPageType
              )}`}
            />
            {isCorporate && type === LandingPageType.PACKAGES && (
              <HowPackagesWork
                corpPackagesNameVariant={corpMarketplacePackagesNameVariant}
              />
            )}
          </Box>
        )}
      />
    </>
  );
};

export default LandingPageNavigation;
